'use client';

import type { Updater, UseQueryOptions } from '@tanstack/react-query';
import type {
  GetProductManagementQuery,
  GetProductManagementQueryVariables,
} from 'generated/graphql';
import { useGetProductManagementQuery } from 'generated/graphql';
import type { ClientError } from 'graphql-request';
import { graphqlClient, queryClient } from 'lib/api';
import useProductIdParam from 'utils/useProductIdParam';

export const getProductManagementQueryKey = (productId: string) =>
  ['GetProductManagement', { productId }] as const;

export const updateProductManagement = (
  productId: string,
  cb: Updater<GetProductManagementQuery | undefined, GetProductManagementQuery>
) => {
  queryClient.setQueryData<GetProductManagementQuery>(getProductManagementQueryKey(productId), cb);
};

export const useInvalidateProductManagement = () => {
  const productId = useProductIdParam();

  return (updater?: Updater<GetProductManagementQuery | undefined, GetProductManagementQuery>) => {
    if (updater) {
      updateProductManagement(productId, updater);
    }
    return refetchProductManagementQuery(productId);
  };
};

export const refetchProductManagementQuery = (productId: string) => {
  const queryKey = getProductManagementQueryKey(productId);

  return queryClient.refetchQueries({ exact: true, queryKey });
};

export function useCurrentProductManagement() {
  const productId = useProductIdParam();
  return useGetProductManagement({ productId });
}

export default function useGetProductManagement(
  variables: GetProductManagementQueryVariables,
  options?: Omit<UseQueryOptions<GetProductManagementQuery, ClientError>, 'queryKey'> & {
    queryKey?: UseQueryOptions<GetProductManagementQuery, ClientError>['queryKey'];
  },
  headers?: RequestInit['headers']
) {
  return useGetProductManagementQuery(
    graphqlClient,
    variables,
    {
      enabled: !!variables.productId,
      ...options,
    },
    headers
  );
}
